import React, { useRef } from 'react';
import { ReactComponent as ChevronSelectorIcon } from 'assets/icons_3/arrows/chevron-selector-vertical.svg';
import { ReactComponent as MapPinIcon } from 'assets/icons_3/marker-pin.svg';
import { useAppSelector, useUserThunkDispatch } from 'hooks';
import classNames from 'classnames';
import useAsyncFetch from 'hooks/async-fetch';
import projectService from 'services/api/customer/project-service';
import { userActions } from 'store/user';
import { useLocation, useNavigate } from 'react-router-dom';
import ProjectDropdown from 'components/Project/ProjectSelector/ProjectDropdown/ProjectDropdown';
import styles from './ProjectSelector.module.scss';

const ProjectSelector = () => {
  const [showProjects, setShowProjects] = React.useState(false);
  const [showRegions, setShowRegions] = React.useState(false);
  const user = useAppSelector((state) => state.user);
  const projects = useAppSelector((state) => state.projects);
  const userThunkDispatch = useUserThunkDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const projectsContainerRef = useRef<HTMLDivElement>(null);
  const regionsContainerRef = useRef<HTMLDivElement>(null);

  const [handleChangeProject, changeLoading] = useAsyncFetch<[number], void>({
    async asyncFunc(projectId) {
      await projectService.selectProject(projectId);
      await userThunkDispatch(userActions.fetchProfile());
      // Ресурс из прошлого проекта не должен оставаться открытым в новом
      const locationPathArr = location.pathname.split('/');
      if (locationPathArr.length > 3) {
        navigate(`/${locationPathArr[1]}/${locationPathArr[2]}`);
      }
    },
    errorNote: true,
  });

  const handleClickOutside: EventListener = (e) => {
    if (projectsContainerRef.current
      && !projectsContainerRef.current.contains(e.target as HTMLElement)) {
      setShowProjects(false);
    }
    if (regionsContainerRef.current
      && !regionsContainerRef.current.contains(e.target as HTMLElement)) {
      setShowRegions(false);
    }
  };

  const [handleChangeRegion] = useAsyncFetch<[number | undefined], void>({
    async asyncFunc(regionId) {
      const newRegion = user.regions.find((region) => region.id === regionId);
      if (newRegion) {
        await userThunkDispatch(userActions.changeRegion(newRegion));
        const locationPathArr = location.pathname.split('/');
        if (locationPathArr.length > 3) {
          navigate(`/${locationPathArr[1]}/${locationPathArr[2]}`);
        }
        // navigate('/');
      }
    },
    errorNote: true,
  });

  React.useEffect(() => {
    window.addEventListener('click', handleClickOutside);
    return () => {
      window.removeEventListener('click', handleClickOutside);
    };
  }, []);

  return (
    <div className={styles.projectSelector}>
      <div
        ref={projectsContainerRef}
        className={classNames(styles.menuWrapper, styles.projectsMenuWrapper, {
          [styles.show]: showProjects,
        })}
      >
        <ProjectDropdown
          className={styles.projectsDropdown}
          projectId={user.currentProject?.id}
          disabled={changeLoading}
          projects={projects.list}
          onSelectProject={handleChangeProject}
        />
      </div>
      <div
        ref={regionsContainerRef}
        className={classNames(styles.menuWrapper, styles.regionsMenuWrapper, {
          [styles.show]: showRegions,
        })}
      >
        <button className={styles.toggle} type="button" onClick={() => setShowRegions(!showRegions)} qa-element="region-selector">
          <MapPinIcon />
          <span>{user.region?.name}</span>
          <ChevronSelectorIcon className={styles.chevronSelector} />
        </button>
        <div className={classNames(styles.menu)}>
          {user.regions.map((region) => (
            <button
              key={region.id}
              className={classNames(styles.item, {
                [styles.active]: user.region?.id === region.id,
              })}
              type="button"
              disabled={user.region?.id === region.id}
              onClick={() => handleChangeRegion(region.id)}
              qa-element={`region-${region.id}`}
            >
              {region.name}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProjectSelector;
